import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { RoutingJobResponse, routingJobsApi } from '../../api/routingJobsApi';
import { getTenantModelDefinition, FileRequirement } from '../../modules/tenantRegistry';

type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

interface FileUploadState {
  file?: File;
  storageId?: string;
  status: UploadStatus;
  error?: string;
}

interface CreateTaskModalProps {
  open: boolean;
  depotId: string | null;
  tenantId?: string;
  modelType?: string;
  onClose: () => void;
  onCreated: (task: RoutingJobResponse) => void;
}

const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  open,
  depotId,
  tenantId,
  modelType,
  onClose,
  onCreated,
}) => {
  const tenantDefinition = useMemo(
    () => getTenantModelDefinition(tenantId, modelType),
    [tenantId, modelType]
  );
  const [fileStates, setFileStates] = useState<Record<string, FileUploadState>>(
    buildInitialState(tenantDefinition.requiredFiles)
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    setFileStates(buildInitialState(tenantDefinition.requiredFiles));
    setSubmitError(null);
  }, [tenantDefinition]);

  const resetState = () => {
    setFileStates(buildInitialState(tenantDefinition.requiredFiles));
    setSubmitError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (!submitting) {
      resetState();
      onClose();
    }
  };

  const handleFileChange = async (fileType: string, file: File) => {
    if (!tenantId || !modelType) {
      setFileStates(prev => ({
        ...prev,
        [fileType]: { status: 'error', error: 'Tenant metadata missing' },
      }));
      return;
    }
    setFileStates(prev => ({
      ...prev,
      [fileType]: { status: 'uploading', file, storageId: undefined, error: undefined },
    }));
    try {
      const response = await routingJobsApi.uploadTenantFile(tenantId, modelType, fileType, file);
      setFileStates(prev => ({
        ...prev,
        [fileType]: { status: 'success', file, storageId: response.storageId },
      }));
    } catch (err: any) {
      setFileStates(prev => ({
        ...prev,
        [fileType]: { status: 'error', file, error: err?.message || 'Upload failed' },
      }));
    }
  };

  const canSubmit =
    !!depotId &&
    Object.values(fileStates).every((state) => state.status === 'success' && state.storageId);

  const handleSubmit = async () => {
    if (!depotId || !canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const inputFiles: Record<string, string> = {};
      Object.entries(fileStates).forEach(([fileType, state]) => {
        if (state.storageId) {
          inputFiles[fileType] = state.storageId;
        }
      });
      const createdTask = await routingJobsApi.createDraftTask(depotId, inputFiles);
      resetState();
      onClose();
      onCreated(createdTask);
    } catch (err: any) {
      setSubmitError(err?.message || 'Failed to create draft task');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Draft Routing Task</DialogTitle>
      <DialogContent dividers>
        {!tenantId && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Tenant metadata missing for this depot. Please contact an administrator.
          </Alert>
        )}

        {tenantDefinition.requiredFiles.map(({ id, label }) => {
      const state = fileStates[id] ?? { status: 'idle' as UploadStatus };
      return (
            <Box
              key={id}
              sx={{
                border: '1px dashed',
                borderColor: state.status === 'error' ? 'error.main' : 'divider',
                borderRadius: 1,
                p: 2,
                mb: 2,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle1">{label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {state.file ? state.file.name : 'No file selected'}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  disabled={!tenantId || state.status === 'uploading'}
                >
                  {state.file ? 'Replace File' : 'Upload File'}
                  <input
                    type="file"
                    accept=".csv"
                    hidden
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) {
                        handleFileChange(id, file);
                      }
                    }}
                  />
                </Button>
              </Stack>
              {state.status === 'uploading' && (
                <Box sx={{ mt: 1 }}>
                  <LinearProgress />
                </Box>
              )}
              {state.status === 'success' && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                  <Typography variant="body2" color="success.main">
                    Uploaded
                  </Typography>
                </Stack>
              )}
              {state.status === 'error' && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <ErrorIcon color="error" fontSize="small" />
                  <Typography variant="body2" color="error">
                    {state.error || 'Upload failed'}
                  </Typography>
                </Stack>
              )}
            </Box>
          );
        })}

        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
        >
          {submitting ? 'Creating...' : 'Create Draft'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTaskModal;

function buildInitialState(requirements: FileRequirement[]): Record<string, FileUploadState> {
  const state: Record<string, FileUploadState> = {};
  requirements.forEach(({ id }) => {
    state[id] = { status: 'idle' };
  });
  return state;
}
